import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiMagnifyingGlass, HiArrowPathRoundedSquare, HiQuestionMarkCircle, HiUserGroup, HiShieldCheck, HiLifebuoy } from 'react-icons/hi2';
import SectionTitle from '../components/SectionTitle';
import InfoBox from '../components/InfoBox';
import ContactCard from '../components/ContactCard';
import { navigationItems } from '../data/navigationData';
import { useApp } from '../context/AppContext';

const Home = () => {
  const { t } = useTranslation();
  const { appData } = useApp();
  const isDark = appData.preferences?.darkMode;

  // Get quick access items (skip home, show first 6)
  const quickAccessItems = navigationItems.slice(1, 7);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4 py-8">
        {/* App Logo */}
        <div className="flex justify-center mb-6">
          <img
            src={isDark ? '/FPRA dark mode.png' : '/FPRA white mode.png'}
            alt="FPRA Logo"
            className="h-24 w-auto object-contain"
          />
        </div>
        
        <h1 className="text-3xl md:text-4xl font-bold text-teal-700 dark:text-teal-400 mb-4 text-center">
          {t('home.title')}
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 font-medium text-center">
          {t('home.subtitle')}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          {t('home.edition')}
        </p>
      </div>

      {/* Disclaimer */}
      <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
        <p className="text-sm text-gray-700 dark:text-gray-300 italic">
          {t('home.disclaimer')}
        </p>
      </div>

      {/* Interactive Features - Compact */}
      <section>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{t('home.interactiveToolsTitle')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/search"
            className="flex items-center gap-3 p-3 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/30 dark:to-teal-800/20 rounded-lg hover:shadow-md transition-all group border border-teal-200 dark:border-teal-800"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-teal-600 dark:bg-teal-600 rounded-lg flex items-center justify-center">
              <HiMagnifyingGlass className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('search.title')}</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('home.searchDescription')}</p>
            </div>
          </Link>

          <Link
            to="/wizards"
            className="flex items-center gap-3 p-3 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/20 rounded-lg hover:shadow-md transition-all group border border-orange-200 dark:border-orange-800"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 dark:bg-orange-600 rounded-lg flex items-center justify-center">
              <HiArrowPathRoundedSquare className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('wizards.title')}</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('home.wizardsDescription')}</p>
            </div>
          </Link>
        </div>
      </section>

      {/* Quick Access Grid - Compact */}
      <section>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{t('home.quickAccessTitle')}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {quickAccessItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.id}
                to={item.path}
                className="block p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-md transition-all group"
              >
                <Icon className="h-6 w-6 text-teal-600 dark:text-teal-400 mb-2" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t(item.labelKey)}
                </h3>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Why this guide */}
      <section>
        <SectionTitle level={2} icon={HiQuestionMarkCircle}>{t('home.whyTitle')}</SectionTitle>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          {t('home.whyContent')}
        </p>
      </section>

      {/* Who is it for */}
      <section>
        <SectionTitle level={2} icon={HiUserGroup}>{t('home.whoTitle')}</SectionTitle>
        <ul className="space-y-2">
          {t('home.whoItems', { returnObjects: true }).map((item, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="text-teal-600 dark:text-teal-400 flex-shrink-0 mt-1">•</span>
              <span className="text-gray-700 dark:text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Principles */}
      <section>
        <SectionTitle level={2} icon={HiShieldCheck}>{t('home.principlesTitle')}</SectionTitle>
        <ul className="space-y-2">
          {t('home.principlesItems', { returnObjects: true }).map((item, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="text-teal-600 dark:text-teal-400 flex-shrink-0 mt-1">•</span>
              <span className="text-gray-700 dark:text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Good reflex */}
      <InfoBox type="reflexe" title={t('home.goodReflexTitle')}>
        <p>{t('home.goodReflexContent')}</p>
      </InfoBox>

      {/* Need help */}
      <section>
        <SectionTitle level={2} icon={HiLifebuoy}>{t('home.needHelpTitle')}</SectionTitle>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
          {t('home.needHelpContent')}
        </p>
      </section>

      {/* Emergency Contact Card */}
      <ContactCard variant="emergency" />
    </div>
  );
};

export default Home;
